import React, { useMemo, useState } from 'react';
import {
  ArrowUpRight,
  BookOpen,
  Brain,
  CheckCircle2,
  Cloud,
  Code,
  Filter,
  Globe,
  Layers,
  Rocket,
  Search,
  Shield,
  Sparkles,
  Terminal,
  Users,
  X,
} from 'lucide-react';

const data = {
  metadata: {
    lastUpdated: '2026-02-20',
    perspective: 'Curated directory with official vendor links for validation',
  },
  categories: [
    'IDE Pairing',
    'Agentic Builders',
    'Code Search',
    'Testing & QA',
    'Documentation',
    'Security',
    'Cloud & DevOps',
    'UI Generation',
    'Open Source',
    'Enterprise',
  ],
  toolTypes: ['IDE', 'CLI', 'Web App', 'Platform', 'Extension'],
  maturityLevels: ['Enterprise-ready', 'Fast-scaling', 'Open-source', 'Preview'],
  featuredIds: [
    'github-copilot',
    'cursor',
    'amazon-q-developer',
    'sourcegraph-cody',
    'claude-code',
    'lovable',
    'vercel-v0',
  ],
  tools: [
    {
      id: 'github-copilot',
      name: 'GitHub Copilot',
      provider: 'GitHub + Microsoft',
      summary: 'AI pair programmer embedded in popular IDEs with chat, refactors, and code suggestions.',
      categories: ['IDE Pairing', 'Enterprise'],
      type: 'IDE',
      maturity: 'Enterprise-ready',
      pricing: {
        model: 'Subscription',
        startingAt: '$10/month',
        freeTier: 'Free for verified students/OSS maintainers',
      },
      bestFor: ['Daily coding assistance', 'Large teams', 'Cross-language support'],
      highlights: ['Inline completions', 'Chat in IDE', 'PR summaries (GitHub)'],
      integrations: ['VS Code', 'JetBrains', 'Neovim', 'Visual Studio', 'GitHub'],
      links: {
        website: 'https://github.com/features/copilot',
        docs: 'https://docs.github.com/copilot',
        pricing: 'https://github.com/features/copilot#pricing',
      },
    },
    {
      id: 'cursor',
      name: 'Cursor',
      provider: 'Cursor',
      summary: 'AI-native code editor for multi-file edits, refactors, and rapid iteration.',
      categories: ['IDE Pairing', 'Agentic Builders'],
      type: 'IDE',
      maturity: 'Fast-scaling',
      pricing: {
        model: 'Freemium',
        startingAt: 'Free / paid plans',
        freeTier: 'Starter plan with limits',
      },
      bestFor: ['Repo-wide edits', 'Startups', 'Rapid prototyping'],
      highlights: ['Multi-file edits', 'In-editor chat', 'Codebase indexing'],
      integrations: ['Built-in editor', 'GitHub', 'Terminal'],
      links: {
        website: 'https://cursor.com',
        docs: 'https://docs.cursor.com',
        pricing: 'https://cursor.com/pricing',
      },
    },
    {
      id: 'amazon-q-developer',
      name: 'Amazon Q Developer',
      provider: 'Amazon Web Services',
      summary: 'AWS-native coding assistant for cloud development and modernization.',
      categories: ['Cloud & DevOps', 'Security', 'IDE Pairing'],
      type: 'Extension',
      maturity: 'Enterprise-ready',
      pricing: {
        model: 'Tiered',
        startingAt: 'Free tier / paid plans',
        freeTier: 'Yes',
      },
      bestFor: ['AWS-first teams', 'Infra-as-code', 'Security-aware coding'],
      highlights: ['AWS service context', 'Security scanning', 'Modernization guidance'],
      integrations: ['VS Code', 'JetBrains', 'AWS Console'],
      links: {
        website: 'https://aws.amazon.com/q/developer/',
        docs: 'https://docs.aws.amazon.com/q/',
        pricing: 'https://aws.amazon.com/q/pricing/',
      },
    },
    {
      id: 'gemini-code-assist',
      name: 'Gemini Code Assist',
      provider: 'Google Cloud',
      summary: 'IDE assistant with Google Cloud context for app development and modernization.',
      categories: ['Cloud & DevOps', 'IDE Pairing'],
      type: 'Extension',
      maturity: 'Enterprise-ready',
      pricing: {
        model: 'Tiered',
        startingAt: 'Free / enterprise plans',
        freeTier: 'Limited free tier',
      },
      bestFor: ['GCP teams', 'Cloud apps', 'Data + ML workflows'],
      highlights: ['Cloud-native guidance', 'IDE chat', 'GCP context'],
      integrations: ['VS Code', 'JetBrains', 'Google Cloud Console'],
      links: {
        website: 'https://cloud.google.com/products/gemini/code-assist',
        docs: 'https://cloud.google.com/gemini/docs',
        pricing: 'https://cloud.google.com/pricing',
      },
    },
    {
      id: 'sourcegraph-cody',
      name: 'Sourcegraph Cody',
      provider: 'Sourcegraph',
      summary: 'AI assistant built on code search and repo intelligence for large codebases.',
      categories: ['Code Search', 'IDE Pairing', 'Enterprise'],
      type: 'Extension',
      maturity: 'Enterprise-ready',
      pricing: {
        model: 'Freemium',
        startingAt: 'Free / paid plans',
        freeTier: 'Yes',
      },
      bestFor: ['Large monorepos', 'Enterprise teams', 'Codebase discovery'],
      highlights: ['Code search + AI', 'Context control', 'Enterprise governance'],
      integrations: ['VS Code', 'JetBrains', 'GitHub', 'GitLab'],
      links: {
        website: 'https://sourcegraph.com/cody',
        docs: 'https://docs.sourcegraph.com/cody',
        pricing: 'https://sourcegraph.com/pricing',
      },
    },
    {
      id: 'jetbrains-ai',
      name: 'JetBrains AI Assistant',
      provider: 'JetBrains',
      summary: 'AI assistance integrated across JetBrains IDEs for refactors, docs, and tests.',
      categories: ['IDE Pairing', 'Testing & QA'],
      type: 'IDE',
      maturity: 'Enterprise-ready',
      pricing: {
        model: 'Subscription',
        startingAt: 'Paid add-on or included plans',
        freeTier: 'Trial included',
      },
      bestFor: ['JetBrains users', 'Refactoring workflows', 'Polyglot teams'],
      highlights: ['IDE-native', 'Language-aware refactors', 'Doc + test generation'],
      integrations: ['IntelliJ', 'PyCharm', 'WebStorm', 'Fleet'],
      links: {
        website: 'https://www.jetbrains.com/ai/',
        docs: 'https://www.jetbrains.com/help/ai/',
        pricing: 'https://www.jetbrains.com/ai/buy/',
      },
    },
    {
      id: 'windsurf',
      name: 'Windsurf',
      provider: 'Codeium',
      summary: 'AI editor focused on workflow automation and code generation.',
      categories: ['IDE Pairing', 'Agentic Builders'],
      type: 'IDE',
      maturity: 'Fast-scaling',
      pricing: {
        model: 'Freemium',
        startingAt: 'Free / paid plans',
        freeTier: 'Yes',
      },
      bestFor: ['Rapid iteration', 'Automated flows', 'Small teams'],
      highlights: ['Multi-file edits', 'Agent chat', 'Codeium models'],
      integrations: ['Built-in editor', 'GitHub'],
      links: {
        website: 'https://codeium.com/windsurf',
        docs: 'https://codeium.com/docs',
        pricing: 'https://codeium.com/pricing',
      },
    },
    {
      id: 'codeium',
      name: 'Codeium',
      provider: 'Exafunction',
      summary: 'Free AI coding assistant with broad IDE support and enterprise offerings.',
      categories: ['IDE Pairing', 'Open Source'],
      type: 'Extension',
      maturity: 'Enterprise-ready',
      pricing: {
        model: 'Freemium',
        startingAt: 'Free / enterprise',
        freeTier: 'Yes',
      },
      bestFor: ['Cost-conscious teams', 'Wide IDE coverage', 'Privacy controls'],
      highlights: ['Unlimited personal usage', 'Self-host options', 'Team analytics'],
      integrations: ['VS Code', 'JetBrains', 'Vim', 'Emacs'],
      links: {
        website: 'https://codeium.com',
        docs: 'https://codeium.com/documentation',
        pricing: 'https://codeium.com/pricing',
      },
    },
    {
      id: 'tabnine',
      name: 'Tabnine',
      provider: 'Tabnine',
      summary: 'Enterprise-friendly AI completion with privacy controls and deployment options.',
      categories: ['IDE Pairing', 'Security'],
      type: 'Extension',
      maturity: 'Enterprise-ready',
      pricing: {
        model: 'Tiered',
        startingAt: 'Free / paid plans',
        freeTier: 'Yes',
      },
      bestFor: ['Regulated environments', 'On-prem/VPC', 'Policy controls'],
      highlights: ['Private deployments', 'Team analytics', 'Policy guardrails'],
      integrations: ['VS Code', 'JetBrains', 'Eclipse', 'Vim'],
      links: {
        website: 'https://www.tabnine.com',
        docs: 'https://docs.tabnine.com',
        pricing: 'https://www.tabnine.com/pricing',
      },
    },
    {
      id: 'replit-ghostwriter',
      name: 'Replit Ghostwriter',
      provider: 'Replit',
      summary: 'AI assistance inside Replit for cloud-based, collaborative development.',
      categories: ['IDE Pairing', 'Cloud & DevOps'],
      type: 'Web App',
      maturity: 'Fast-scaling',
      pricing: {
        model: 'Subscription',
        startingAt: 'Included with Replit plans',
        freeTier: 'Limited free usage',
      },
      bestFor: ['Education', 'Hackathons', 'Instant deploys'],
      highlights: ['Realtime collaboration', 'In-browser IDE', 'Templates'],
      integrations: ['Replit IDE', 'GitHub'],
      links: {
        website: 'https://replit.com/ghostwriter',
        docs: 'https://docs.replit.com',
        pricing: 'https://replit.com/pricing',
      },
    },
    {
      id: 'devin',
      name: 'Devin',
      provider: 'Cognition',
      summary: 'Autonomous software engineer for end-to-end tasks in real dev environments.',
      categories: ['Agentic Builders'],
      type: 'Platform',
      maturity: 'Fast-scaling',
      pricing: {
        model: 'Enterprise',
        startingAt: 'Contact sales',
        freeTier: 'No',
      },
      bestFor: ['Long-running tasks', 'Autonomous execution', 'Backlog automation'],
      highlights: ['Plans + executes tasks', 'Runs tests', 'Works in real repos'],
      integrations: ['GitHub', 'CLI environments'],
      links: {
        website: 'https://www.cognition.ai',
        docs: 'https://docs.cognition.ai',
        pricing: 'https://www.cognition.ai',
      },
    },
    {
      id: 'openhands',
      name: 'OpenHands',
      provider: 'Open-source community',
      summary: 'Open-source agent platform for coding tasks, tooling, and workflows.',
      categories: ['Agentic Builders', 'Open Source'],
      type: 'Platform',
      maturity: 'Open-source',
      pricing: {
        model: 'Open source',
        startingAt: 'Free (self-host)',
        freeTier: 'Yes',
      },
      bestFor: ['Custom agent stacks', 'Self-hosted workflows', 'Research teams'],
      highlights: ['Model-agnostic', 'Tool calling', 'Flexible hosting'],
      integrations: ['CLI', 'GitHub', 'Custom tools'],
      links: {
        website: 'https://github.com/All-Hands-AI/OpenHands',
        docs: 'https://docs.all-hands.dev',
        pricing: 'https://github.com/All-Hands-AI/OpenHands',
      },
    },
    {
      id: 'aider',
      name: 'Aider',
      provider: 'Open-source community',
      summary: 'Terminal-first AI pair programmer that edits your repo with git-aware diffs.',
      categories: ['Open Source', 'Agentic Builders'],
      type: 'CLI',
      maturity: 'Open-source',
      pricing: {
        model: 'Open source',
        startingAt: 'Free',
        freeTier: 'Yes',
      },
      bestFor: ['CLI-first devs', 'Git workflows', 'Automation'],
      highlights: ['Git-aware edits', 'Scriptable workflows', 'Model flexibility'],
      integrations: ['CLI', 'Git', 'Local LLMs'],
      links: {
        website: 'https://aider.chat',
        docs: 'https://aider.chat/docs',
        pricing: 'https://aider.chat/docs/faq',
      },
    },
    {
      id: 'continue',
      name: 'Continue',
      provider: 'Continue.dev',
      summary: 'Open-source AI assistant for VS Code and JetBrains with custom model routing.',
      categories: ['Open Source', 'IDE Pairing'],
      type: 'Extension',
      maturity: 'Open-source',
      pricing: {
        model: 'Open source',
        startingAt: 'Free',
        freeTier: 'Yes',
      },
      bestFor: ['BYO model keys', 'Team configs', 'Custom prompt stacks'],
      highlights: ['Model routing', 'Custom prompts', 'Team shareable configs'],
      integrations: ['VS Code', 'JetBrains', 'Local LLMs'],
      links: {
        website: 'https://continue.dev',
        docs: 'https://docs.continue.dev',
        pricing: 'https://continue.dev/pricing',
      },
    },
    {
      id: 'swe-agent',
      name: 'SWE-agent',
      provider: 'Open-source community',
      summary: 'Agent framework that solves GitHub issues with reproducible task execution.',
      categories: ['Agentic Builders', 'Open Source'],
      type: 'CLI',
      maturity: 'Open-source',
      pricing: {
        model: 'Open source',
        startingAt: 'Free',
        freeTier: 'Yes',
      },
      bestFor: ['Issue triage', 'Automated bug fixes', 'Research workflows'],
      highlights: ['Task environment replay', 'Benchmarks support', 'Tool execution'],
      integrations: ['GitHub', 'Docker', 'CLI'],
      links: {
        website: 'https://github.com/swe-agent/swe-agent',
        docs: 'https://github.com/swe-agent/swe-agent',
        pricing: 'https://github.com/swe-agent/swe-agent',
      },
    },
    {
      id: 'mintlify-writer',
      name: 'Mintlify Writer',
      provider: 'Mintlify',
      summary: 'Documentation assistant for API docs, guides, and code explanations.',
      categories: ['Documentation'],
      type: 'Extension',
      maturity: 'Fast-scaling',
      pricing: {
        model: 'Freemium',
        startingAt: 'Free / paid plans',
        freeTier: 'Yes',
      },
      bestFor: ['Doc teams', 'API platforms', 'Open-source projects'],
      highlights: ['API doc automation', 'Style consistency', 'Inline editing'],
      integrations: ['VS Code', 'GitHub', 'GitLab'],
      links: {
        website: 'https://mintlify.com',
        docs: 'https://docs.mintlify.com',
        pricing: 'https://mintlify.com/pricing',
      },
    },
    {
      id: 'snyk-code-ai',
      name: 'Snyk Code',
      provider: 'Snyk',
      summary: 'Security scanning with AI guidance for vulnerability fixes and prioritization.',
      categories: ['Security', 'Cloud & DevOps'],
      type: 'Platform',
      maturity: 'Enterprise-ready',
      pricing: {
        model: 'Tiered',
        startingAt: 'Free / enterprise',
        freeTier: 'Yes',
      },
      bestFor: ['DevSecOps teams', 'Security governance', 'Risk reporting'],
      highlights: ['Code scanning', 'Supply-chain alerts', 'Policy enforcement'],
      integrations: ['GitHub', 'GitLab', 'Bitbucket', 'CI/CD'],
      links: {
        website: 'https://snyk.io/product/snyk-code/',
        docs: 'https://docs.snyk.io',
        pricing: 'https://snyk.io/plans/',
      },
    },
    {
      id: 'vercel-v0',
      name: 'v0',
      provider: 'Vercel',
      summary: 'Generates UI from prompts with React + Tailwind output.',
      categories: ['UI Generation'],
      type: 'Web App',
      maturity: 'Fast-scaling',
      pricing: {
        model: 'Usage-based',
        startingAt: 'Free / paid credits',
        freeTier: 'Yes',
      },
      bestFor: ['Design systems', 'Landing pages', 'UI scaffolding'],
      highlights: ['High-fidelity UI', 'Tailwind-ready', 'Fast iteration loops'],
      integrations: ['Next.js', 'Vercel', 'Figma imports'],
      links: {
        website: 'https://v0.dev',
        docs: 'https://v0.dev/docs',
        pricing: 'https://v0.dev/pricing',
      },
    },
    {
      id: 'stackblitz-bolt',
      name: 'Bolt',
      provider: 'StackBlitz',
      summary: 'Prompt-to-app generation in the browser with live previews.',
      categories: ['UI Generation', 'Agentic Builders'],
      type: 'Web App',
      maturity: 'Fast-scaling',
      pricing: {
        model: 'Usage-based',
        startingAt: 'Free / paid tiers',
        freeTier: 'Yes',
      },
      bestFor: ['Frontend prototypes', 'Quick demos', 'Hackathon builds'],
      highlights: ['Instant preview', 'Browser-native', 'Shareable links'],
      integrations: ['StackBlitz IDE', 'GitHub', 'Netlify'],
      links: {
        website: 'https://bolt.new',
        docs: 'https://developer.stackblitz.com',
        pricing: 'https://stackblitz.com/pricing',
      },
    },
    {
      id: 'claude-code',
      name: 'Claude Code',
      provider: 'Anthropic',
      summary: 'CLI-based coding assistant that helps plan, edit, and review code with model context.',
      categories: ['Agentic Builders', 'IDE Pairing'],
      type: 'CLI',
      maturity: 'Fast-scaling',
      pricing: {
        model: 'Usage-based',
        startingAt: 'API usage pricing',
        freeTier: 'No',
      },
      bestFor: ['CLI-centric workflows', 'Code reviews', 'Task automation'],
      highlights: ['Terminal-first UX', 'Large-context reasoning', 'Tool-friendly workflows'],
      integrations: ['CLI', 'Git', 'Custom scripts'],
      links: {
        website: 'https://www.anthropic.com/claude',
        docs: 'https://docs.anthropic.com',
        pricing: 'https://www.anthropic.com/pricing',
      },
    },
    {
      id: 'lovable',
      name: 'Lovable',
      provider: 'Lovable',
      summary: 'AI app builder that turns product requirements into full-stack code and deployable apps.',
      categories: ['Agentic Builders', 'UI Generation'],
      type: 'Web App',
      maturity: 'Fast-scaling',
      pricing: {
        model: 'Freemium',
        startingAt: 'Free / paid plans',
        freeTier: 'Yes',
      },
      bestFor: ['MVPs', 'Product prototypes', 'Full-stack scaffolding'],
      highlights: ['Spec-to-app workflow', 'Full-stack output', 'Deploy-ready projects'],
      integrations: ['Web app', 'GitHub', 'Vercel'],
      links: {
        website: 'https://www.lovable.dev',
        docs: 'https://docs.lovable.dev',
        pricing: 'https://www.lovable.dev/pricing',
      },
    },
    {
      id: 'coderabbit',
      name: 'CodeRabbit',
      provider: 'CodeRabbit',
      summary: 'AI code review assistant that comments on pull requests with actionable feedback.',
      categories: ['Testing & QA', 'Documentation', 'Enterprise'],
      type: 'Platform',
      maturity: 'Enterprise-ready',
      pricing: {
        model: 'Subscription',
        startingAt: 'Free / paid plans',
        freeTier: 'Yes',
      },
      bestFor: ['PR review automation', 'Faster code quality feedback', 'Team QA workflows'],
      highlights: ['PR summaries', 'Inline suggestions', 'Multi-repo coverage'],
      integrations: ['GitHub', 'GitLab', 'Bitbucket'],
      links: {
        website: 'https://coderabbit.ai',
        docs: 'https://coderabbit.ai/docs',
        pricing: 'https://coderabbit.ai/pricing',
      },
    },
    {
      id: 'qodo',
      name: 'Qodo (CodiumAI)',
      provider: 'Qodo',
      summary: 'AI-powered test generation and code quality assistant for developers.',
      categories: ['Testing & QA', 'IDE Pairing'],
      type: 'Extension',
      maturity: 'Fast-scaling',
      pricing: {
        model: 'Freemium',
        startingAt: 'Free / paid plans',
        freeTier: 'Yes',
      },
      bestFor: ['Unit test generation', 'Coverage improvements', 'Regression prevention'],
      highlights: ['Test suggestions', 'Code quality checks', 'IDE guidance'],
      integrations: ['VS Code', 'JetBrains', 'GitHub'],
      links: {
        website: 'https://www.qodo.ai',
        docs: 'https://docs.qodo.ai',
        pricing: 'https://www.qodo.ai/pricing',
      },
    },
    {
      id: 'diffblue-cover',
      name: 'Diffblue Cover',
      provider: 'Diffblue',
      summary: 'Automated Java unit test generation for enterprise codebases.',
      categories: ['Testing & QA', 'Enterprise'],
      type: 'Platform',
      maturity: 'Enterprise-ready',
      pricing: {
        model: 'Enterprise',
        startingAt: 'Contact sales',
        freeTier: 'No',
      },
      bestFor: ['Java teams', 'Legacy codebases', 'Test automation'],
      highlights: ['Java-focused', 'Automated unit tests', 'Enterprise workflows'],
      integrations: ['IntelliJ', 'Jenkins', 'Maven/Gradle'],
      links: {
        website: 'https://www.diffblue.com',
        docs: 'https://docs.diffblue.com',
        pricing: 'https://www.diffblue.com/pricing',
      },
    },
    {
      id: 'semgrep-assistant',
      name: 'Semgrep',
      provider: 'Semgrep',
      summary: 'Code scanning platform with AI-powered security and code quality guidance.',
      categories: ['Security', 'Cloud & DevOps', 'Enterprise'],
      type: 'Platform',
      maturity: 'Enterprise-ready',
      pricing: {
        model: 'Tiered',
        startingAt: 'Free / paid plans',
        freeTier: 'Yes',
      },
      bestFor: ['Security teams', 'CI/CD scanning', 'Policy enforcement'],
      highlights: ['Static analysis', 'Security rules', 'CI/CD integration'],
      integrations: ['GitHub', 'GitLab', 'Bitbucket', 'CI/CD'],
      links: {
        website: 'https://semgrep.dev',
        docs: 'https://semgrep.dev/docs',
        pricing: 'https://semgrep.dev/pricing',
      },
    },
    {
      id: 'deepsource',
      name: 'DeepSource',
      provider: 'DeepSource',
      summary: 'Automated code review and static analysis for maintainability and security.',
      categories: ['Testing & QA', 'Security'],
      type: 'Platform',
      maturity: 'Fast-scaling',
      pricing: {
        model: 'Freemium',
        startingAt: 'Free / paid plans',
        freeTier: 'Yes',
      },
      bestFor: ['Code quality reviews', 'CI/CD checks', 'Multi-language repos'],
      highlights: ['Automated code review', 'Quality gates', 'CI/CD integration'],
      integrations: ['GitHub', 'GitLab', 'Bitbucket'],
      links: {
        website: 'https://deepsource.com',
        docs: 'https://docs.deepsource.com',
        pricing: 'https://deepsource.com/pricing',
      },
    },
    {
      id: 'gitbook-ai',
      name: 'GitBook AI',
      provider: 'GitBook',
      summary: 'Documentation platform with AI search, drafting, and knowledge base workflows.',
      categories: ['Documentation', 'Enterprise'],
      type: 'Web App',
      maturity: 'Fast-scaling',
      pricing: {
        model: 'Freemium',
        startingAt: 'Free / paid plans',
        freeTier: 'Yes',
      },
      bestFor: ['Team documentation', 'Developer portals', 'Knowledge bases'],
      highlights: ['AI search', 'Doc generation', 'Content workflows'],
      integrations: ['GitHub', 'GitLab', 'Slack'],
      links: {
        website: 'https://www.gitbook.com',
        docs: 'https://docs.gitbook.com',
        pricing: 'https://www.gitbook.com/pricing',
      },
    },
    {
      id: 'supermaven',
      name: 'Supermaven',
      provider: 'Supermaven',
      summary: 'Fast AI code completion focused on low latency and long-context suggestions.',
      categories: ['IDE Pairing'],
      type: 'Extension',
      maturity: 'Fast-scaling',
      pricing: {
        model: 'Freemium',
        startingAt: 'Free / paid plans',
        freeTier: 'Yes',
      },
      bestFor: ['Low-latency completions', 'Daily coding', 'Fast iteration loops'],
      highlights: ['Low-latency suggestions', 'Long-context completions', 'IDE integration'],
      integrations: ['VS Code', 'JetBrains'],
      links: {
        website: 'https://supermaven.com',
        docs: 'https://supermaven.com/docs',
        pricing: 'https://supermaven.com/pricing',
      },
    },
    {
      id: 'warp-ai',
      name: 'Warp AI',
      provider: 'Warp',
      summary: 'AI-enhanced terminal for command generation, explanations, and workflow shortcuts.',
      categories: ['Cloud & DevOps', 'Agentic Builders'],
      type: 'CLI',
      maturity: 'Fast-scaling',
      pricing: {
        model: 'Freemium',
        startingAt: 'Free / paid plans',
        freeTier: 'Yes',
      },
      bestFor: ['Terminal workflows', 'DevOps automation', 'Command discovery'],
      highlights: ['AI command suggestions', 'Workflow blocks', 'Team sharing'],
      integrations: ['Terminal', 'SSH', 'Cloud CLIs'],
      links: {
        website: 'https://www.warp.dev',
        docs: 'https://docs.warp.dev',
        pricing: 'https://www.warp.dev/pricing',
      },
    },
  ],
};

const recommendationStacks = [
  {
    title: 'Everyday Pairing Stack',
    description: 'IDE assistant + repo search + security guardrails for daily shipping.',
    items: ['GitHub Copilot', 'Sourcegraph Cody', 'Snyk Code'],
    icon: Sparkles,
  },
  {
    title: 'Autonomous Delivery Stack',
    description: 'Agentic builder + CLI automation + UI generator for fast product iterations.',
    items: ['Devin', 'Aider', 'v0'],
    icon: Rocket,
  },
  {
    title: 'Cloud Modernization Stack',
    description: 'Cloud-native copilots that understand infra, policy, and deployment workflows.',
    items: ['Amazon Q Developer', 'Gemini Code Assist', 'Snyk Code'],
    icon: Cloud,
  },
];

const signals = [
  {
    title: 'Deep repo context',
    description: 'Leading tools build dependency graphs to answer multi-file questions more accurately.',
    icon: Layers,
  },
  {
    title: 'Governance & privacy',
    description: 'Policy controls, audit logs, and private hosting options are now expected.',
    icon: Shield,
  },
  {
    title: 'Agentic workflows',
    description: 'Assistants are shifting from suggestions to executing complete tasks end-to-end.',
    icon: Brain,
  },
];

function AIAgentsDirectory() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedMaturity, setSelectedMaturity] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const filteredTools = useMemo(() => {
    return data.tools.filter((tool) => {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        tool.name.toLowerCase().includes(search) ||
        tool.provider.toLowerCase().includes(search) ||
        tool.summary.toLowerCase().includes(search) ||
        tool.categories.some((category) => category.toLowerCase().includes(search)) ||
        tool.bestFor.some((item) => item.toLowerCase().includes(search)) ||
        tool.highlights.some((item) => item.toLowerCase().includes(search)) ||
        tool.integrations.some((item) => item.toLowerCase().includes(search));

      const matchesCategory = selectedCategory === 'all' || tool.categories.includes(selectedCategory);
      const matchesType = selectedType === 'all' || tool.type === selectedType;
      const matchesMaturity = selectedMaturity === 'all' || tool.maturity === selectedMaturity;

      return matchesSearch && matchesCategory && matchesType && matchesMaturity;
    });
  }, [searchTerm, selectedCategory, selectedType, selectedMaturity]);

  const featuredTools = data.tools.filter((tool) => data.featuredIds.includes(tool.id));

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.25),_transparent_55%)]" />
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-purple-500/10 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 py-16 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3 text-xs uppercase tracking-[0.2em] text-slate-400">
            <Sparkles className="h-4 w-4 text-cyan-400" />
            {data.metadata.perspective}
          </div>
          <h1 className="mt-6 text-4xl md:text-6xl font-semibold leading-tight">
            AI Dev Tools Atlas 2026
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-300 mx-auto">
            A focused directory of the most credible AI coding assistants as of February 2026. Every entry links to
            official docs so teams can validate claims, pricing, and availability.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-sm text-slate-400">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              Last updated {data.metadata.lastUpdated}
            </span>
            <span className="flex items-center gap-2">
              <Users className="h-4 w-4 text-cyan-400" />
              {data.tools.length} tools with official links
            </span>
            <span className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-blue-400" />
              Official docs cited per vendor
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-12">
        <div className="grid gap-6 md:grid-cols-3">
          {signals.map((signal) => (
            <div key={signal.title} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
              <signal.icon className="h-6 w-6 text-blue-400" />
              <h3 className="mt-4 text-lg font-semibold">{signal.title}</h3>
              <p className="mt-2 text-sm text-slate-400">{signal.description}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {recommendationStacks.map((stack) => (
            <div
              key={stack.title}
              className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900/80 to-slate-950 p-6"
            >
              <stack.icon className="h-6 w-6 text-cyan-400" />
              <h3 className="mt-4 text-lg font-semibold">{stack.title}</h3>
              <p className="mt-2 text-sm text-slate-400">{stack.description}</p>
              <ul className="mt-4 space-y-2 text-sm text-slate-300">
                {stack.items.map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold">Featured tools</h2>
              <p className="mt-2 text-sm text-slate-400">Top picks with consistent product updates and strong adoption.</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Terminal className="h-4 w-4" />
              Curated for reliability + completeness
            </div>
          </div>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            {featuredTools.map((tool) => (
              <div key={tool.id} className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-semibold">{tool.name}</h3>
                    <p className="text-sm text-slate-400">{tool.provider}</p>
                  </div>
                  <span className="rounded-full bg-blue-500/10 px-3 py-1 text-xs text-blue-300">{tool.type}</span>
                </div>
                <p className="mt-4 text-sm text-slate-300">{tool.summary}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {tool.highlights.slice(0, 3).map((item) => (
                    <span key={item} className="rounded-full bg-slate-800 px-3 py-1 text-xs text-slate-300">
                      {item}
                    </span>
                  ))}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <Brain className="h-4 w-4 text-purple-400" />
                    {tool.maturity}
                  </span>
                  <span className="flex items-center gap-1">
                    <Globe className="h-4 w-4 text-blue-400" />
                    {tool.pricing.model}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-3 text-xs">
                  {Object.entries(tool.links).map(([label, url]) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-300"
                    >
                      {label}
                      <ArrowUpRight className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold">Explore the full 2026 stack</h2>
              <p className="mt-2 text-sm text-slate-400">
                Filter by category, surface type, and maturity. Every listing includes a vendor link and pricing source.
              </p>
            </div>
            <button
              onClick={() => setShowFilters((prev) => !prev)}
              className="flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-xs text-slate-300 hover:border-slate-500"
            >
              {showFilters ? <X className="h-4 w-4" /> : <Filter className="h-4 w-4" />}
              {showFilters ? 'Hide filters' : 'Show filters'}
            </button>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by tool, provider, capability, integration..."
                  className="w-full rounded-xl border border-slate-800 bg-slate-950 px-11 py-3 text-sm text-white placeholder:text-slate-500 focus:border-slate-600 focus:outline-none"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-3 text-xs">
                <select
                  className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-slate-300"
                  value={selectedCategory}
                  onChange={(event) => setSelectedCategory(event.target.value)}
                >
                  <option value="all">All categories</option>
                  {data.categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                <select
                  className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-slate-300"
                  value={selectedType}
                  onChange={(event) => setSelectedType(event.target.value)}
                >
                  <option value="all">All types</option>
                  {data.toolTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <select
                  className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-slate-300"
                  value={selectedMaturity}
                  onChange={(event) => setSelectedMaturity(event.target.value)}
                >
                  <option value="all">All maturity levels</option>
                  {data.maturityLevels.map((maturity) => (
                    <option key={maturity} value={maturity}>
                      {maturity}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            {filteredTools.map((tool) => (
              <div key={tool.id} className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-xl font-semibold">{tool.name}</h3>
                    <p className="text-sm text-slate-400">{tool.provider}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="rounded-full bg-slate-800 px-3 py-1 text-xs text-slate-300">{tool.type}</span>
                    <span className="text-xs text-slate-500">Updated {data.metadata.lastUpdated}</span>
                  </div>
                </div>
                <p className="mt-4 text-sm text-slate-300">{tool.summary}</p>

                <div className="mt-4 flex flex-wrap gap-2">
                  {tool.categories.map((category) => (
                    <span key={category} className="rounded-full bg-blue-500/10 px-3 py-1 text-xs text-blue-300">
                      {category}
                    </span>
                  ))}
                </div>

                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Best for</p>
                    <ul className="mt-2 space-y-1 text-xs text-slate-300">
                      {tool.bestFor.map((item) => (
                        <li key={item} className="flex items-center gap-2">
                          <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                    <p className="text-xs uppercase tracking-wide text-slate-500">Highlights</p>
                    <ul className="mt-2 space-y-1 text-xs text-slate-300">
                      {tool.highlights.map((item) => (
                        <li key={item} className="flex items-center gap-2">
                          <Sparkles className="h-3 w-3 text-cyan-400" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
                  <span className="flex items-center gap-2">
                    <Shield className="h-3 w-3 text-blue-400" />
                    {tool.maturity}
                  </span>
                  <span className="flex items-center gap-2">
                    <Code className="h-3 w-3 text-emerald-400" />
                    {tool.type}
                  </span>
                  <span className="flex items-center gap-2">
                    <Terminal className="h-3 w-3 text-purple-400" />
                    {tool.pricing.model}
                  </span>
                </div>

                <div className="mt-4 rounded-xl border border-slate-800 bg-slate-950/70 p-4 text-xs text-slate-300">
                  <p className="text-slate-400">Pricing snapshot</p>
                  <p className="mt-1 text-sm text-white">{tool.pricing.startingAt}</p>
                  <p className="mt-1 text-slate-400">{tool.pricing.freeTier}</p>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {tool.integrations.map((integration) => (
                    <span
                      key={integration}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-3 py-1 text-xs text-slate-300"
                    >
                      <Globe className="h-3 w-3" />
                      {integration}
                    </span>
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap gap-3 text-xs">
                  {Object.entries(tool.links).map(([label, url]) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-300"
                    >
                      {label}
                      <ArrowUpRight className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 text-sm text-slate-400">
            Showing {filteredTools.length} of {data.tools.length} tools
          </div>
        </div>

        <div className="mt-16 rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900/80 via-slate-950 to-slate-900 p-8">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl font-semibold">How to pick your 2026 stack</h2>
              <p className="mt-2 text-sm text-slate-400">
                Start with a fast IDE copilot, add one agentic builder for multi-step tasks, then layer in security or
                documentation automation. Keep at least one open-source option to stay model-agnostic.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-slate-300">
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-800 px-3 py-1">
                <Brain className="h-4 w-4 text-purple-400" />
                Pick tools that can run tests
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-800 px-3 py-1">
                <Shield className="h-4 w-4 text-blue-400" />
                Validate privacy + governance
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-800 px-3 py-1">
                <Terminal className="h-4 w-4 text-emerald-400" />
                Automate repetitive CLI flows
              </span>
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-slate-800 py-10 text-center text-xs text-slate-500">
        Built for teams who need credible, up-to-date AI tooling. Verify pricing and terms with each vendor before
        purchase.
      </footer>
    </div>
  );
}

export default AIAgentsDirectory;
